import * as $ from "jquery";
import { util, $format } from "@ax/office-shared-lib";
import {
    OperationManager,
    EventLogger,
    ErrorManager,
    LogEventOptions,
    AppActions,
    ILinkOptions,
    ErrorOptions,
    ShowErrorOptions
} from "@ax/office-telemetry-common";

/* tslint:disable:no-reserved-keywords */

/** Contains operation definitions and corresponding functionality with which to begin and end each operation. */
export class OperationHandler {

    /** Contains functionality for the "loading-applet-*" operation. Occurs when loading an applet. */
    public readonly loadingApplet = {

        /** The ID of the "loading-applet-*" event. */
        get id() { return "loading-applet-{0}"; },

        /**
         * Begins the "loading-applet-*" operation.
         * @param arg The operation ID format parameter.
         * @param message The message to display indicating work is taking place for the "loading-applet-*" operation.
         */
        begin: (arg: string, message: string): void => {
            this.operationManager.beginOperation($format(this.loadingApplet.id, arg), message);
        },

        /**
         * Updates the "loading-applet-*" operation.
         * @param arg The operation ID format parameter.
         * @param message The message to display indicating work has been updated for the "loading-applet-*" operation.
         */
        update: (arg: string, message: string): void => {
            this.operationManager.updateOperation($format(this.loadingApplet.id, arg), message);
        },

        /**
         * Ends the "loading-applet-*" operation.
         * @param arg The operation ID format parameter.
         * @param message The message to display indicating work has completed for the "loading-applet-*" operation.
         */
        end: (arg: string, message: string): void => {
            this.operationManager.endOperation($format(this.loadingApplet.id, arg), message);
        }
    };

    /** Contains functionality for the "retrieving-configuration" operation. Occurs when retreiving configuration. */
    public readonly retrievingConfiguration = {

        /** The ID of the "retrieving-configuration" event. */
        get id() { return "retrieving-configuration"; },

        /**
         * Begins the "retrieving-configuration" operation.
         * @param message The message to display indicating work is taking place for the "retrieving-configuration" operation.
         */
        begin: (message: string): void => {
            this.operationManager.beginOperation(this.retrievingConfiguration.id, message);
        },

        /**
         * Updates the "retrieving-configuration" operation.
         * @param message The message to display indicating work has been updated for the "retrieving-configuration" operation.
         */
        update: (message: string): void => {
            this.operationManager.updateOperation(this.retrievingConfiguration.id, message);
        },

        /**
         * Ends the "retrieving-configuration" operation.
         * @param message The message to display indicating work has completed for the "retrieving-configuration" operation.
         */
        end: (message: string): void => {
            this.operationManager.endOperation(this.retrievingConfiguration.id, message);
        }
    };

    /** Contains functionality for the "registrations-retrieval" operation. Occurs when retrieving applet and resource registrations. */
    public readonly registrationsRetrieval = {

        /** The ID of the "registrations-retrieval" event. */
        get id() { return "registrations-retrieval"; },

        /**
         * Begins the "registrations-retrieval" operation.
         * @param message The message to display indicating work is taking place for the "registrations-retrieval" operation.
         */
        begin: (message: string): void => {
            this.operationManager.beginOperation(this.registrationsRetrieval.id, message);
        },

        /**
         * Updates the "registrations-retrieval" operation.
         * @param message The message to display indicating work has been updated for the "registrations-retrieval" operation.
         */
        update: (message: string): void => {
            this.operationManager.updateOperation(this.registrationsRetrieval.id, message);
        },

        /**
         * Ends the "registrations-retrieval" operation.
         * @param message The message to display indicating work has completed for the "registrations-retrieval" operation.
         */
        end: (message: string): void => {
            this.operationManager.endOperation(this.registrationsRetrieval.id, message);
        }
    };

    /** Contains functionality for the "Authentication" operation. Occurs when verifying user authentication. */
    public readonly authentication = {

        /** The ID of the "Authentication" event. */
        get id() { return "Authentication"; },

        /**
         * Begins the "Authentication" operation.
         * @param message The message to display indicating work is taking place for the "Authentication" operation.
         */
        begin: (message: string): void => {
            this.operationManager.beginOperation(this.authentication.id, message);
        },

        /**
         * Updates the "Authentication" operation.
         * @param message The message to display indicating work has been updated for the "Authentication" operation.
         */
        update: (message: string): void => {
            this.operationManager.updateOperation(this.authentication.id, message);
        },

        /**
         * Ends the "Authentication" operation.
         * @param message The message to display indicating work has completed for the "Authentication" operation.
         */
        end: (message: string): void => {
            this.operationManager.endOperation(this.authentication.id, message);
        }
    };

    /** Contains functionality for the "retrieving-environments" operation. Occurs when retrieving environment information. */
    public readonly retrievingEnvironments = {

        /** The ID of the "retrieving-environments" event. */
        get id() { return "retrieving-environments"; },

        /**
         * Begins the "retrieving-environments" operation.
         * @param message The message to display indicating work is taking place for the "retrieving-environments" operation.
         */
        begin: (message: string): void => {
            this.operationManager.beginOperation(this.retrievingEnvironments.id, message);
        },

        /**
         * Updates the "retrieving-environments" operation.
         * @param message The message to display indicating work has been updated for the "retrieving-environments" operation.
         */
        update: (message: string): void => {
            this.operationManager.updateOperation(this.retrievingEnvironments.id, message);
        },

        /**
         * Ends the "retrieving-environments" operation.
         * @param message The message to display indicating work has completed for the "retrieving-environments" operation.
         */
        end: (message: string): void => {
            this.operationManager.endOperation(this.retrievingEnvironments.id, message);
        }
    };

    /** Contains functionality for the "signing-out" operation. Occurs when the user chooses to sign out of the add-in. */
    public readonly signingOut = {

        /** The ID of the "signing-out" event. */
        get id() { return "signing-out"; },

        /**
         * Begins the "signing-out" operation.
         * @param message The message to display indicating work is taking place for the "signing-out" operation.
         */
        begin: (message: string): void => {
            this.operationManager.beginOperation(this.signingOut.id, message);
        },

        /**
         * Updates the "signing-out" operation.
         * @param message The message to display indicating work has been updated for the "signing-out" operation.
         */
        update: (message: string): void => {
            this.operationManager.updateOperation(this.signingOut.id, message);
        },

        /**
         * Ends the "signing-out" operation.
         * @param message The message to display indicating work has completed for the "signing-out" operation.
         */
        end: (message: string): void => {
            this.operationManager.endOperation(this.signingOut.id, message);
        }
    };

    /**
     * @param operationManager The object used to manage operations.
     */
    constructor(private operationManager: OperationManager) { }
}

/** Contains operation definitions and corresponding functionality with which log events. */
export class EventHandler {

    /** Contains functionality for the "dom-event" operation. The event logged when DOM events occur. */
    public readonly domEvent = {

        /** The ID of the "dom-event" operation. */
        get id() { return "dom-event"; },

        /**
         * Logs the "dom-event" telemetry event.
         * @param options The optional parameters to adjust how the "dom-event" event is logged.
         */
        log: (options?: any): void => {
            this.eventLogger.logEvent(this.domEvent.id, options);
        }
    };

    /** Contains functionality for the "auth-type-determined" operation. The event logged once the authentication type is determined. */
    public readonly authTypeDetermined = {

        /** The ID of the "auth-type-determined" operation. */
        get id() { return "auth-type-determined"; },

        /**
         * Logs the "auth-type-determined" telemetry event.
         * @param authType The type of authentication being used (e.g. OAuth vs. Cookie). Telemetry classification of 'System Metadata'. Enumeration type of 'appState.AuthType'. Sample data: Cookie, OAuth.
         */
        log: (authType: string | number): void => {
            this.eventLogger.logEvent(this.authTypeDetermined.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "AuthType": authType };  return details; })()));
        }
    };

    /** Contains functionality for the "oauth-login-mechanism-determined" operation. The event logged once the authentication type is determined. */
    public readonly oauthLoginMechanismDetermined = {

        /** The ID of the "oauth-login-mechanism-determined" operation. */
        get id() { return "oauth-login-mechanism-determined"; },

        /**
         * Logs the "oauth-login-mechanism-determined" telemetry event.
         * @param authMechanism The type of mechanism being used to prompt the user for authentication (e.g. window, dialog, or inline). Telemetry classification of 'System Metadata'. Enumeration type of 'authentication.OAuthLoginMechanism'. Sample data: Window, Dialog, Inline.
         */
        log: (authMechanism: string | number): void => {
            this.eventLogger.logEvent(this.oauthLoginMechanismDetermined.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "AuthMechanism": authMechanism };  return details; })()));
        }
    };

    /** Contains functionality for the "oauth-log-event" operation. The event logged during oauth authentication when warnings or errors are encountered. */
    public readonly oauthLogEvent = {

        /** The ID of the "oauth-log-event" operation. */
        get id() { return "oauth-log-event"; },

        /**
         * Logs the "oauth-log-event" telemetry event.
         * @param message The MSAL JS OAuth warning or error message logged. Telemetry classification of 'System Metadata'. Sample data: Fri, 23 Aug 2019 00:03:02 GMT:1.0.17-WARNING: IdToken has invalid aud field.
         */
        log: (message: string): void => {
            this.eventLogger.logEvent(this.oauthLogEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "Message": message };  return details; })()));
        }
    };

    /** Contains functionality for the "oauth-silent-signin-event" operation. The event logged during initial oauth authentication if a silent sign in attempt is encountered. */
    public readonly oauthSilentSigninEvent = {

        /** The ID of the "oauth-silent-signin-event" operation. */
        get id() { return "oauth-silent-signin-event"; },

        /**
         * Logs the "oauth-silent-signin-event" telemetry event.
         * @param aadErrorCode The authentication error code. Telemetry classification of 'System Metadata'. Sample data: AADSTS50058, Authentication error, AADSTS70002.
         * @param msalErrorCode The MSAL error code. Telemetry classification of 'System Metadata'. Sample data: interaction_required, Token renewal failed.
         */
        log: (aadErrorCode: string, msalErrorCode: string): void => {
            this.eventLogger.logEvent(this.oauthSilentSigninEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "AadErrorCode": aadErrorCode, "MsalErrorCode": msalErrorCode };  return details; })()));
        }
    };

    /** Contains functionality for the "cookie-sign-in-event" operation. The event logged when cookie sign in is attempted. */
    public readonly cookieSignInEvent = {

        /** The ID of the "cookie-sign-in-event" operation. */
        get id() { return "cookie-sign-in-event"; },

        /**
         * Logs the "cookie-sign-in-event" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.cookieSignInEvent.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "cookie-sign-out-event" operation. The event logged when cookie sign out is attempted. */
    public readonly cookieSignOutEvent = {

        /** The ID of the "cookie-sign-out-event" operation. */
        get id() { return "cookie-sign-out-event"; },

        /**
         * Logs the "cookie-sign-out-event" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.cookieSignOutEvent.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "oAuth-sign-in-event" operation. The event logged when OAuth sign in is attempted. */
    public readonly oAuthSignInEvent = {

        /** The ID of the "oAuth-sign-in-event" operation. */
        get id() { return "oAuth-sign-in-event"; },

        /**
         * Logs the "oAuth-sign-in-event" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.oAuthSignInEvent.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "oAuth-sign-out-event" operation. The event logged when OAuth sign out is attempted. */
    public readonly oAuthSignOutEvent = {

        /** The ID of the "oAuth-sign-out-event" operation. */
        get id() { return "oAuth-sign-out-event"; },

        /**
         * Logs the "oAuth-sign-out-event" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.oAuthSignOutEvent.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "app-init" operation. The event logged once the app start initializing. */
    public readonly appInit = {

        /** The ID of the "app-init" operation. */
        get id() { return "app-init"; },

        /**
         * Logs the "app-init" telemetry event.
         * @param documentid The ID of the document. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param dateCreated The date the document was created. Telemetry classification of 'System Metadata'. Sample data: 2016-01-02T01:02:03.4567890Z, 2017-02-03T04:05:06.7890123Z.
         * @param generationActivityID The ID of the activity used to generated the document. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         */
        log: (documentid: string, dateCreated: string, generationActivityID: string): void => {
            this.eventLogger.logEvent(this.appInit.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "DocumentID": documentid, "DateCreated": dateCreated, "GenerationActivityID": generationActivityID };  return details; })()));
        }
    };

    /** Contains functionality for the "powerapps-version-determined" operation. The event logged once the PowerApps version is determined. */
    public readonly powerappsVersionDetermined = {

        /** The ID of the "powerapps-version-determined" operation. */
        get id() { return "powerapps-version-determined"; },

        /**
         * Logs the "powerapps-version-determined" telemetry event.
         * @param powerAppsVersion The version of PowerApps being used (e.g. CDS1 vs CDS2). Telemetry classification of 'System Metadata'. Enumeration type of 'powerApps.PowerAppsVersion'. Sample data: CDS1, CDS2.
         */
        log: (powerAppsVersion: string | number): void => {
            this.eventLogger.logEvent(this.powerappsVersionDetermined.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "PowerAppsVersion": powerAppsVersion };  return details; })()));
        }
    };

    /** Contains functionality for the "powerapps-version-unknown" operation. The event logged when the PowerApps version is not known or is unhandled. */
    public readonly powerappsVersionUnknown = {

        /** The ID of the "powerapps-version-unknown" operation. */
        get id() { return "powerapps-version-unknown"; },

        /**
         * Logs the "powerapps-version-unknown" telemetry event.
         * @param powerAppsVersion The invalid version of PowerApps being used (e.g. CDS1 vs CDS2). Telemetry classification of 'System Metadata'. Enumeration type of 'powerApps.PowerAppsVersion'. Sample data: CDS1, CDS2.
         */
        log: (powerAppsVersion: string | number): void => {
            this.eventLogger.logEvent(this.powerappsVersionUnknown.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "PowerAppsVersion": powerAppsVersion };  return details; })()));
        }
    };

    /** Contains functionality for the "user-correlation" operation. The event logged to correlate a unique user to the session. */
    public readonly userCorrelation = {

        /** The ID of the "user-correlation" operation. */
        get id() { return "user-correlation"; },

        /**
         * Logs the "user-correlation" telemetry event.
         * @param tenantid The ID of the tenant. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param userid The ID of the user. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         */
        log: (tenantid: string, userid: string): void => {
            this.eventLogger.logEvent(this.userCorrelation.id, util.telemetry.createUsageTelemetryOptions((function() { const details = { "TenantID": tenantid, "UserID": userid };  return details; })()));
        }
    };

    /** Contains functionality for the "resource-loaded" operation. The event logged when a resource is loaded. */
    public readonly resourceLoaded = {

        /** The ID of the "resource-loaded" operation. */
        get id() { return "resource-loaded"; },

        /**
         * Logs the "resource-loaded" telemetry event.
         * @param resourceid The ID of the resource. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: Microsoft.Dynamics.Platform.Integration.Office.DataResource, authentication-resource, config-resource.
         * @param url The URL of the resource. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/AxODataInteraction.html, https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Public/CheckAuthentication.html, https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Public/Config.html, https://officeapps.dynamics.com/applets/v1/ODataInteraction.html.
         */
        log: (resourceid: string, url: string): void => {
            this.eventLogger.logEvent(this.resourceLoaded.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "ResourceID": resourceid, "Url": url };  return details; })()));
        }
    };

    /** Contains functionality for the "resource-registration-load-failed" operation. The event logged when resource registrations fail to load. */
    public readonly resourceRegistrationLoadFailed = {

        /** The ID of the "resource-registration-load-failed" operation. */
        get id() { return "resource-registration-load-failed"; },

        /**
         * Logs the "resource-registration-load-failed" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.resourceRegistrationLoadFailed.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "app-state-updated" operation. The event logged whenever app state is updated. */
    public readonly appStateUpdated = {

        /** The ID of the "app-state-updated" operation. */
        get id() { return "app-state-updated"; },

        /**
         * Logs the "app-state-updated" telemetry event.
         * @param authenticationState Indicates the state of authentication. Telemetry classification of 'System Metadata'. Enumeration type of 'appState.AuthenticationState'. Sample data: uninitialized, notAuthenticated, authenticating.
         * @param authorizationState Indicates the state of authorization. Telemetry classification of 'System Metadata'. Enumeration type of 'appState.AuthorizationState'. Sample data: unknown, notAuthorized.
         * @param appletsReadyState Indicates the state of ready applets (roughly how many are ready). Telemetry classification of 'System Metadata'. Enumeration type of 'appState.AppletsReadyState'. Sample data: none, single, multiple.
         * @param registrationsRetrieved Indicates whether applet and resource registrations have been retrieved. Telemetry classification of 'System Metadata'. Sample data: true, false.
         * @param appletsLoadingCount Indicates the number of applets that are currently loading. Telemetry classification of 'System Metadata'. Sample data: 1, 10, 25.
         * @param featuresLogDomEvents Indicates whether the app is logging DOM events. Telemetry classification of 'System Metadata'. Sample data: true, false.
         * @param featuresLogTelemetry Indicates whether the app is logging telemetry events. Telemetry classification of 'System Metadata'. Sample data: true, false.
         * @param manualSignInAttempted Indicates whether manual sign in has been attemped. Telemetry classification of 'System Metadata'. Sample data: true, false.
         */
        log: (authenticationState: string | number, authorizationState: string | number, appletsReadyState: string | number, registrationsRetrieved: boolean, appletsLoadingCount: number, featuresLogDomEvents: boolean, featuresLogTelemetry: boolean, manualSignInAttempted: boolean): void => {
            this.eventLogger.logEvent(this.appStateUpdated.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "AuthenticationState": authenticationState, "AuthorizationState": authorizationState, "AppletsReadyState": appletsReadyState, "RegistrationsRetrieved": registrationsRetrieved, "AppletsLoadingCount": appletsLoadingCount, "Features.LogDomEvents": featuresLogDomEvents, "Features.LogTelemetry": featuresLogTelemetry, "ManualSignInAttempted": manualSignInAttempted };  return details; })()));
        }
    };

    /** Contains functionality for the "options-saved-telemetry-event" operation. The event logged when options are saved. */
    public readonly optionsSavedTelemetryEvent = {

        /** The ID of the "options-saved-telemetry-event" operation. */
        get id() { return "options-saved-telemetry-event"; },

        /**
         * Logs the "options-saved-telemetry-event" telemetry event.
         * @param changedAppOptions The options that changed. Telemetry classification of 'System Metadata'. Sample data: ["Language"], ["HostName","Language"].
         * @param changedAppletOptions The qualified applet options that changed. Telemetry classification of 'System Metadata'. Sample data: ["Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.CellRefreshLimit","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.ConfirmPublishOnCreate","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.ConfirmPublishOnUpdate","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.SectionBindings"], ["Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.AutoSizeColumns","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.RefreshOnOpen","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.CellRefreshLimit","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.DataOptions.EnablePublishOrder","Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet.SectionBindings"].
         */
        log: (changedAppOptions: string[], changedAppletOptions: string[]): void => {
            this.eventLogger.logEvent(this.optionsSavedTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "ChangedAppOptions": JSON.stringify(changedAppOptions), "ChangedAppletOptions": JSON.stringify(changedAppletOptions) };  return details; })()));
        }
    };

    /** Contains functionality for the "reconnection-requested" operation. The event logged when an applet requests the add-in to reconnect. */
    public readonly reconnectionRequested = {

        /** The ID of the "reconnection-requested" operation. */
        get id() { return "reconnection-requested"; },

        /**
         * Logs the "reconnection-requested" telemetry event.
         * @param willRedirect Indicates whether the add-in will redirect. Telemetry classification of 'System Metadata'. Sample data: true, false.
         */
        log: (willRedirect: boolean): void => {
            this.eventLogger.logEvent(this.reconnectionRequested.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "WillRedirect": willRedirect };  return details; })()));
        }
    };

    /** Contains functionality for the "exception-telemetry-event" operation. The event logged when an unhandled exception occurs within the app or applet. */
    public readonly exceptionTelemetryEvent = {

        /** The ID of the "exception-telemetry-event" operation. */
        get id() { return "exception-telemetry-event"; },

        /**
         * Logs the "exception-telemetry-event" telemetry event.
         * @param message The exception's message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Unable to get property 'isValid' of undefined or null reference, Unable to get property 'closed' of undefined or null reference.
         * @param urlLocation The location of the file from which the exception originated. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js, https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js.
         * @param line The line number within the file from which the exception originated. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         * @param column The column number within the file from which the exception originated. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         * @param error The JSON serialized exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: {"description":"Unable to get property 'isValid' of undefined or null reference","number":-2146823281,"stack":"TypeError: Unable to get property 'isValid' of undefined or null reference\n   at ui (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:15438)\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:1944)\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:19712)\n   at OSF.DDA.issueAsyncResult (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:58174)\n   at onComplete (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69650)\n   at Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:95016)\n   at e[] (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69387)\n   at Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/exce, {"description":"Unable to get property 'closed' of undefined or null reference","number":-2146823281,"stack":"TypeError: Unable to get property 'closed' of undefined or null reference\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:37:5273)"}.
         * @param stack The stack trace for the encountered exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: TypeError: Unable to get property 'isValid' of undefined or null reference\nat ui (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:15438)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:1944)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:19712)\nat OSF.DDA.issueAsyncResult (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:58174)\nat onComplete (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69650)\nat Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:95016)\nat e[] (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69387)\nat Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:77100)\nat ci (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:4, TypeError: Unable to get property 'closed' of undefined or null reference\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:37:5273).
         * @param errorCode Code that identifies the exception. Telemetry classification of 'System Metadata'. Sample data: UnhandledAppError, UnhandledAppletError.
         * @param severity Indicates how severe the error is. Telemetry classification of 'System Metadata'. Enumeration type of 'util.telemetry.ErrorSeverity'. Sample data: High, Low.
         */
        log: (message: string, urlLocation: string, line: number, column: number, error: string, stack: string, errorCode: string, severity: string | number): void => {
            this.eventLogger.logEvent(this.exceptionTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "Message": message, "URLLocation": urlLocation, "Line": line, "Column": column, "Error": error, "Stack": stack, "ErrorCode": errorCode, "Severity": severity };  return details; })()));
        }
    };

    /** Contains functionality for the "error-telemetry-event" operation. The event logged when an error is shown or added within the app or applet. */
    public readonly errorTelemetryEvent = {

        /** The ID of the "error-telemetry-event" operation. */
        get id() { return "error-telemetry-event"; },

        /**
         * Logs the "error-telemetry-event" telemetry event.
         * @param telemetryDetails The error message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: No applet registrations found, Error Loading Metadata\n\nDetails:\nThe request timed out waiting for a response..
         * @param stack The stack trace for the encountered error. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Error\nat h (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:43:2429)\nat i (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:13:17664)\nat catch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:85:13532)\nat t._executePromiseCallback (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15575)\nat t._executeClosestPromiseCatch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:16274)\nat t.prototype.reject (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15439)\nat i.prototype.responseReceived (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:49:12448)\nat t.prototype.listen (https://usnconeboxax1aos.clo, Error\nat nr (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:23176)\nat ir (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:24304)\nat i (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:19:10224)\nat r (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:32066)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:33422)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17692)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17411)\nat each (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/Scripts/ext/jquery-2.1.4.min.js:2:2875)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17388).
         * @param errorCode Code that identifies the unique error. Telemetry classification of 'System Metadata'. Sample data: ResolvingLabelsFailed, PublishingCreatedRowFailed, DataCacheWriteFailed.
         * @param severity Indicates how severe the error is. Telemetry classification of 'System Metadata'. Enumeration type of 'util.telemetry.ErrorSeverity'. Sample data: High, Low.
         */
        log: (telemetryDetails: string, stack: string, errorCode: string, severity: string | number): void => {
            this.eventLogger.logEvent(this.errorTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "TelemetryDetails": telemetryDetails, "Stack": stack, "ErrorCode": errorCode, "Severity": severity };  return details; })()));
        }
    };

    /** Contains functionality for the "user-error-telemetry-event" operation. The event logged when a user error is shown or added within the app or applet. */
    public readonly userErrorTelemetryEvent = {

        /** The ID of the "user-error-telemetry-event" operation. */
        get id() { return "user-error-telemetry-event"; },

        /**
         * Logs the "user-error-telemetry-event" telemetry event.
         * @param telemetryDetails The error message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: No applet registrations found, Error Loading Metadata\n\nDetails:\nThe request timed out waiting for a response..
         * @param stack The stack trace for the encountered error. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Error\nat h (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:43:2429)\nat i (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:13:17664)\nat catch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:85:13532)\nat t._executePromiseCallback (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15575)\nat t._executeClosestPromiseCatch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:16274)\nat t.prototype.reject (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15439)\nat i.prototype.responseReceived (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:49:12448)\nat t.prototype.listen (https://usnconeboxax1aos.clo, Error\nat nr (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:23176)\nat ir (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:24304)\nat i (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:19:10224)\nat r (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:32066)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:33422)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17692)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17411)\nat each (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/Scripts/ext/jquery-2.1.4.min.js:2:2875)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17388).
         * @param errorCode Code that identifies the unique error. Telemetry classification of 'System Metadata'. Sample data: ResolvingLabelsFailed, PublishingCreatedRowFailed, DataCacheWriteFailed.
         * @param severity Indicates how severe the error is. Telemetry classification of 'System Metadata'. Enumeration type of 'util.telemetry.ErrorSeverity'. Sample data: High, Low.
         */
        log: (telemetryDetails: string, stack: string, errorCode: string, severity: string | number): void => {
            this.eventLogger.logEvent(this.userErrorTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "TelemetryDetails": telemetryDetails, "Stack": stack, "ErrorCode": errorCode, "Severity": severity };  return details; })()));
        }
    };

    /** Contains functionality for the "system-error-telemetry-event" operation. The event logged when a system error is shown or added within the app or applet. */
    public readonly systemErrorTelemetryEvent = {

        /** The ID of the "system-error-telemetry-event" operation. */
        get id() { return "system-error-telemetry-event"; },

        /**
         * Logs the "system-error-telemetry-event" telemetry event.
         * @param telemetryDetails The error message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: No applet registrations found, Error Loading Metadata\n\nDetails:\nThe request timed out waiting for a response..
         * @param stack The stack trace for the encountered error. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Error\nat h (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:43:2429)\nat i (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:13:17664)\nat catch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:85:13532)\nat t._executePromiseCallback (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15575)\nat t._executeClosestPromiseCatch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:16274)\nat t.prototype.reject (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15439)\nat i.prototype.responseReceived (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:49:12448)\nat t.prototype.listen (https://usnconeboxax1aos.clo, Error\nat nr (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:23176)\nat ir (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:24304)\nat i (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:19:10224)\nat r (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:32066)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:33422)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17692)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17411)\nat each (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/Scripts/ext/jquery-2.1.4.min.js:2:2875)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17388).
         * @param errorCode Code that identifies the unique error. Telemetry classification of 'System Metadata'. Sample data: ResolvingLabelsFailed, PublishingCreatedRowFailed, DataCacheWriteFailed.
         * @param severity Indicates how severe the error is. Telemetry classification of 'System Metadata'. Enumeration type of 'util.telemetry.ErrorSeverity'. Sample data: High, Low.
         */
        log: (telemetryDetails: string, stack: string, errorCode: string, severity: string | number): void => {
            this.eventLogger.logEvent(this.systemErrorTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "TelemetryDetails": telemetryDetails, "Stack": stack, "ErrorCode": errorCode, "Severity": severity };  return details; })()));
        }
    };

    /** Contains functionality for the "office-error-telemetry-event" operation. The event logged when an office error is shown or added within the app or applet. */
    public readonly officeErrorTelemetryEvent = {

        /** The ID of the "office-error-telemetry-event" operation. */
        get id() { return "office-error-telemetry-event"; },

        /**
         * Logs the "office-error-telemetry-event" telemetry event.
         * @param telemetryDetails The error message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: No applet registrations found, Error Loading Metadata\n\nDetails:\nThe request timed out waiting for a response..
         * @param stack The stack trace for the encountered error. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Error\nat h (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:43:2429)\nat i (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:13:17664)\nat catch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:85:13532)\nat t._executePromiseCallback (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15575)\nat t._executeClosestPromiseCatch (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:16274)\nat t.prototype.reject (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:7:15439)\nat i.prototype.responseReceived (https://usnconeboxax1aos.cloud.onebox.dynamics.com/OfficeApps/Applets/ExcelDataConnector/ExcelApp.min.js:49:12448)\nat t.prototype.listen (https://usnconeboxax1aos.clo, Error\nat nr (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:23176)\nat ir (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:24304)\nat i (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:19:10224)\nat r (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:32066)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:33422)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17692)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17411)\nat each (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/Scripts/ext/jquery-2.1.4.min.js:2:2875)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:49:17388).
         * @param errorCode Code that identifies the unique error. Telemetry classification of 'System Metadata'. Sample data: ResolvingLabelsFailed, PublishingCreatedRowFailed, DataCacheWriteFailed.
         * @param severity Indicates how severe the error is. Telemetry classification of 'System Metadata'. Enumeration type of 'util.telemetry.ErrorSeverity'. Sample data: High, Low.
         */
        log: (telemetryDetails: string, stack: string, errorCode: string, severity: string | number): void => {
            this.eventLogger.logEvent(this.officeErrorTelemetryEvent.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "TelemetryDetails": telemetryDetails, "Stack": stack, "ErrorCode": errorCode, "Severity": severity };  return details; })()));
        }
    };

    /** Contains functionality for the "document-*-event" operation. The event logged when a document event is fired. */
    public readonly documentEvent = {

        /** The ID format of the "document-*-event" operation. */
        get idFormat() { return "document-{0}-event"; },

        /**
         * Logs the "document-*-event" non-telemetry event.
         * @param arg The event ID format parameter.
         */
        log: (arg: string): void => {
            this.eventLogger.logEvent($format(this.documentEvent.idFormat, arg));
        }
    };

    /** Contains functionality for the "binding-*-event" operation. The event logged when a binding event is fired. */
    public readonly bindingEvent = {

        /** The ID format of the "binding-*-event" operation. */
        get idFormat() { return "binding-{0}-event"; },

        /**
         * Logs the "binding-*-event" non-telemetry event.
         * @param arg The event ID format parameter.
         */
        log: (arg: string): void => {
            this.eventLogger.logEvent($format(this.bindingEvent.idFormat, arg));
        }
    };

    /** Contains functionality for the "data-cache-table-get-workaround" operation. The event logged when a workaround is applied when getting a data cache table. */
    public readonly dataCacheTableGetWorkaround = {

        /** The ID of the "data-cache-table-get-workaround" operation. */
        get id() { return "data-cache-table-get-workaround"; },

        /**
         * Logs the "data-cache-table-get-workaround" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.dataCacheTableGetWorkaround.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "data-cache-table-add-workaround" operation. The event logged when a workaround is applied when adding a data cache table. */
    public readonly dataCacheTableAddWorkaround = {

        /** The ID of the "data-cache-table-add-workaround" operation. */
        get id() { return "data-cache-table-add-workaround"; },

        /**
         * Logs the "data-cache-table-add-workaround" telemetry event.
         */
        log: (): void => {
            this.eventLogger.logEvent(this.dataCacheTableAddWorkaround.id, util.telemetry.createInfoTelemetryOptions());
        }
    };

    /** Contains functionality for the "odata-call-attempted" operation. The event logged whenever a data call is attempted. */
    public readonly odataCallAttempted = {

        /** The ID of the "odata-call-attempted" operation. */
        get id() { return "odata-call-attempted"; },

        /**
         * Logs the "odata-call-attempted" telemetry event.
         * @param sourceID The ID of the originating source
         * @param requestType The type of request made. Telemetry classification of 'System Metadata'. Sample data: GetDataRequest, GetLabelsRequest, PublishDataRequest.
         * @param activityid The OData call activity ID. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param recordCount The number of records involved in the data call. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         */
        log: (sourceID: string, requestType: string, activityid: string, recordCount?: number): void => {
            this.eventLogger.logEvent(this.odataCallAttempted.id, (function() { const options = util.telemetry.createInfoTelemetryOptions((function() { const details = { "RequestType": requestType, "ActivityID": activityid }; if (recordCount !== undefined) { details["RecordCount"] = recordCount; } return details; })()); options.SourceID = sourceID; return options; })());
        }
    };

    /** Contains functionality for the "odata-call-throttled" operation. The event logged whenever a data call is throttled. */
    public readonly odataCallThrottled = {

        /** The ID of the "odata-call-throttled" operation. */
        get id() { return "odata-call-throttled"; },

        /**
         * Logs the "odata-call-throttled" telemetry event.
         * @param sourceID The ID of the originating source
         * @param requestType The type of request made. Telemetry classification of 'System Metadata'. Sample data: GetDataRequest, GetLabelsRequest, PublishDataRequest.
         * @param activityid The OData call activity ID. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param originalActivityID The original OData call activity ID. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param waitTime The amount of time the add-in will wait before retrying the request. Telemetry classification of 'System Metadata'. Sample data: default, 15.
         * @param retryAttempt The retry attempted number. Telemetry classification of 'System Metadata'. Sample data: 1, 3.
         * @param retrying Indicates whether the request will be retried. Telemetry classification of 'System Metadata'. Sample data: true, false.
         * @param recordCount The number of records involved in the data call. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         */
        log: (sourceID: string, requestType: string, activityid: string, originalActivityID: string, waitTime: string, retryAttempt: number, retrying: boolean, recordCount?: number): void => {
            this.eventLogger.logEvent(this.odataCallThrottled.id, (function() { const options = util.telemetry.createInfoTelemetryOptions((function() { const details = { "RequestType": requestType, "ActivityID": activityid, "OriginalActivityID": originalActivityID, "WaitTime": waitTime, "RetryAttempt": retryAttempt, "Retrying": retrying }; if (recordCount !== undefined) { details["RecordCount"] = recordCount; } return details; })()); options.SourceID = sourceID; return options; })());
        }
    };

    /** Contains functionality for the "odata-call-auth-token-updated" operation. The event logged whenever a data call receives new authorization tokens. */
    public readonly odataCallAuthTokenUpdated = {

        /** The ID of the "odata-call-auth-token-updated" operation. */
        get id() { return "odata-call-auth-token-updated"; },

        /**
         * Logs the "odata-call-auth-token-updated" telemetry event.
         * @param sourceID The ID of the originating source
         * @param requestType The type of request made. Telemetry classification of 'System Metadata'. Sample data: GetDataRequest, GetLabelsRequest, PublishDataRequest.
         */
        log: (sourceID: string, requestType: string): void => {
            this.eventLogger.logEvent(this.odataCallAuthTokenUpdated.id, (function() { const options = util.telemetry.createInfoTelemetryOptions((function() { const details = { "RequestType": requestType };  return details; })()); options.SourceID = sourceID; return options; })());
        }
    };

    /** Contains functionality for the "odata-call" operation. The event logged whenever a data call is completed. */
    public readonly odataCall = {

        /** The ID of the "odata-call" operation. */
        get id() { return "odata-call"; },

        /**
         * Logs the "odata-call" telemetry event.
         * @param sourceID The ID of the originating source
         * @param requestType The type of request made. Telemetry classification of 'System Metadata'. Sample data: GetDataRequest, GetLabelsRequest, PublishDataRequest.
         * @param activityid The OData call activity ID. Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF.
         * @param status The status (success or failure) of the OData call. Telemetry classification of 'System Metadata'. Sample data: Success, Failure.
         * @param failureStatusCode The status code of failed response (or 0 if the response was successful). Telemetry classification of 'System Metadata'. Sample data: 0, 403, 500.
         * @param duration The duration of time spent waiting for the OData call return from the server (in milliseconds). Telemetry classification of 'System Metadata'. Sample data: 100, 500, 2000.
         * @param recordCount The number of records involved in the data call. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         */
        log: (sourceID: string, requestType: string, activityid: string, status: string, failureStatusCode: number, duration?: number, recordCount?: number): void => {
            this.eventLogger.logEvent(this.odataCall.id, (function() { const options = util.telemetry.createInfoTelemetryOptions((function() { const details = { "RequestType": requestType, "ActivityID": activityid, "Status": status, "FailureStatusCode": failureStatusCode }; if (duration !== undefined) { details["Duration"] = duration; } if (recordCount !== undefined) { details["RecordCount"] = recordCount; } return details; })()); options.SourceID = sourceID; return options; })());
        }
    };

    /** Contains functionality for the "desired-language-unavailable" operation. The language the user desired to use within the app was not available. */
    public readonly desiredLanguageUnavailable = {

        /** The ID of the "desired-language-unavailable" operation. */
        get id() { return "desired-language-unavailable"; },

        /**
         * Logs the "desired-language-unavailable" telemetry event.
         * @param desiredLanguage The language the user desired to use within the app. Telemetry classification of 'System Metadata'. Sample data: ES-ES, NL-NL, TR-TR.
         * @param workingLanguage The language the user actually ended up using within the app. Telemetry classification of 'System Metadata'. Sample data: ES, NL, TR.
         */
        log: (desiredLanguage: string, workingLanguage: string): void => {
            this.eventLogger.logEvent(this.desiredLanguageUnavailable.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "DesiredLanguage": desiredLanguage, "WorkingLanguage": workingLanguage };  return details; })()));
        }
    };

    /** Contains functionality for the "confirmation-closed" operation. A confirmation prompt was closed. */
    public readonly confirmationClosed = {

        /** The ID of the "confirmation-closed" operation. */
        get id() { return "confirmation-closed"; },

        /**
         * Logs the "confirmation-closed" telemetry event.
         * @param sourceID The ID of the originating source
         * @param userResponse A value indicating if the user accepted the prompt. Telemetry classification of 'System Metadata'. Sample data: true, false.
         * @param duration The number of milliseconds the confirmation was open. Telemetry classification of 'System Metadata'. Sample data: 500, 5000, 20000.
         */
        log: (sourceID: string, userResponse: boolean, duration: number): void => {
            this.eventLogger.logEvent(this.confirmationClosed.id, (function() { const options = util.telemetry.createInfoTelemetryOptions((function() { const details = { "UserResponse": userResponse, "Duration": duration };  return details; })()); options.SourceID = sourceID; return options; })());
        }
    };

    /** Contains functionality for the "applet-requests-throttled" operation. Applet request messages were throttled due to high volume. */
    public readonly appletRequestsThrottled = {

        /** The ID of the "applet-requests-throttled" operation. */
        get id() { return "applet-requests-throttled"; },

        /**
         * Logs the "applet-requests-throttled" telemetry event.
         * @param throttledMessages A JSON serialized property bag keyed by request message type with counts of throttled messages as the value. Telemetry classification of 'System Metadata'. Sample data: {"LogEvent":25}, {"BindingMethod":100,"LogEvent":250}, {"LogEvent":15,"SaveSettings":2,"BindingMethod":550}.
         */
        log: (throttledMessages: string): void => {
            this.eventLogger.logEvent(this.appletRequestsThrottled.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "ThrottledMessages": throttledMessages };  return details; })()));
        }
    };

    /** Contains functionality for the "office-api-failure" operation. Logged whenever an Office API method encounters a failure */
    public readonly officeApiFailure = {

        /** The ID of the "office-api-failure" operation. */
        get id() { return "office-api-failure"; },

        /**
         * Logs the "office-api-failure" telemetry event.
         * @param methodType The type of method that was being executed. Telemetry classification of 'System Metadata'. Enumeration type of 'officeInteraction.MethodType'. Sample data: DocumentEventSubscription, BindingEventSubscription, OfficeAction, DocumentMethod, BindingsMethod, BindingMethod.
         * @param method The name of the Office API method that encountered the error. Telemetry classification of 'System Metadata'. Sample data: getByIdAsync, addRowsAsync, setDataAsync.
         * @param errorCode The Office API's error code. Telemetry classification of 'System Metadata'. Sample data: 5001, 2003, 1001.
         */
        log: (methodType: string | number, method: string, errorCode: number): void => {
            this.eventLogger.logEvent(this.officeApiFailure.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "MethodType": methodType, "Method": method, "ErrorCode": errorCode };  return details; })()));
        }
    };

    /** Contains functionality for the "applet-label-missing" operation. The event logged whenever applet label metadata is successfully retrieved but is missing requested label metadata. */
    public readonly appletLabelMissing = {

        /** The ID of the "applet-label-missing" operation. */
        get id() { return "applet-label-missing"; },

        /**
         * Logs the "applet-label-missing" telemetry event.
         * @param requestedLabelIds The IDs of the labels for which metadata was requested. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: ["@ApplicationPlatform:OfficeAppDataConnectorAppletName"], ["@ApplicationPlatform:OfficeAppDataConnectorAppletName","@ApplicationPlatform:OfficeAppTemplateDesignerAppletName"].
         * @param retrievedLabelResults The result of the labels actually retrieved. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: [{"ID":"@ApplicationPlatform:OfficeAppDataConnectorAppletName","Value":"Data Connector"}], [{"ID":"@ApplicationPlatform:OfficeAppDataConnectorAppletName","Value":"Data Connector"},{"ID":"@ApplicationPlatform:OfficeAppTemplateDesignerAppletName","Value":"Template Designer"}].
         */
        log: (requestedLabelIds: string, retrievedLabelResults: string): void => {
            this.eventLogger.logEvent(this.appletLabelMissing.id, util.telemetry.createInfoTelemetryOptions((function() { const details = { "RequestedLabelIds": requestedLabelIds, "RetrievedLabelResults": retrievedLabelResults };  return details; })()));
        }
    };

    /**
     * @param eventLogger The object used to log events.
     */
    constructor(private eventLogger: EventLogger) { }

    /** Hard disables all events that are used only for DOM eventing (test framework). This operation is non-recoverable. */
    public noopDomEvents(): void {
        this.documentEvent.log = $.noop;
        this.bindingEvent.log = $.noop;
    }
}

/** Contains error definitions and corresponding functionality with which to show or add errors. */
export class ErrorHandler {

    /** Contains functionality for the "AppletLoadTimedOut" operation. The load of an applet timed out. */
    public readonly appletLoadTimedOut = {

        /** The "AppletLoadTimedOut" error code. */
        get code() { return "AppletLoadTimedOut"; },

        /**
         * Shows the "AppletLoadTimedOut" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.appletLoadTimedOut.code, $.extend(true, {}, options));
        }
    }

    /** Contains functionality for the "AppletLoadFailed" operation. An applet failed to load. */
    public readonly appletLoadFailed = {

        /** The "AppletLoadFailed" error code. */
        get code() { return "AppletLoadFailed"; },

        /**
         * Shows the "AppletLoadFailed" error.
         * @param message The error message to show.
         * @param telemetryDetails The reason the applet failed to load. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Invalid app origin of "https://usnconeboxax1aos.cloud.onebox.dynamics.com" received during applet configuration.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, telemetryDetails: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.appletLoadFailed.code, $.extend(true, { TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ TelemetryDetails: telemetryDetails }) }, options));
        }
    }

    /** Contains functionality for the "ResourceNotRegistered" operation. A resource wasn't registered. */
    public readonly resourceNotRegistered = {

        /** The "ResourceNotRegistered" error code. */
        get code() { return "ResourceNotRegistered"; },

        /**
         * Shows the "ResourceNotRegistered" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.resourceNotRegistered.code, $.extend(true, {}, options));
        }
    }

    /** Contains functionality for the "OAuthAuthenticationExpired" operation. An OAuth token expired. */
    public readonly oAuthAuthenticationExpired = {

        /** The "OAuthAuthenticationExpired" error code. */
        get code() { return "OAuthAuthenticationExpired"; },

        /**
         * Shows the "OAuthAuthenticationExpired" error.
         * @param message The error message to show.
         * @param expiredAuthError The authentication expired error code. Telemetry classification of 'System Metadata'. Sample data: AADSTS50058, Authentication error.
         * @param msalErrorCode The MSAL error code. Telemetry classification of 'System Metadata'. Sample data: interaction_required, Token renewal failed.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, expiredAuthError: string, msalErrorCode: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.oAuthAuthenticationExpired.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ ExpiredAuthError: expiredAuthError, MsalErrorCode: msalErrorCode }) }, options));
        }
    }

    /** Contains functionality for the "AuthPageLoginError" operation. Authentication failed on the auth page (Office Online). */
    public readonly authPageLoginError = {

        /** The "AuthPageLoginError" error code. */
        get code() { return "AuthPageLoginError"; },

        /**
         * Shows the "AuthPageLoginError" error.
         * @param message The error message to show.
         * @param authLoginError The authentication error code. Telemetry classification of 'System Metadata'. Sample data: AADSTS50058, Authentication error, AADSTS70002.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, authLoginError: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.authPageLoginError.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ AuthLoginError: authLoginError }) }, options));
        }
    }

    /** Contains functionality for the "AuthPageOpenError" operation. The authentication window failed to open (Office Online). */
    public readonly authPageOpenError = {

        /** The "AuthPageOpenError" error code. */
        get code() { return "AuthPageOpenError"; },

        /**
         * Shows the "AuthPageOpenError" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.authPageOpenError.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "OAuthDialogError" operation. The OAuth dialog encountered an error. */
    public readonly oAuthDialogError = {

        /** The "OAuthDialogError" error code. */
        get code() { return "OAuthDialogError"; },

        /**
         * Shows the "OAuthDialogError" error.
         * @param message The error message to show.
         * @param dialogErrorCode The error code encountered Telemetry classification of 'System Metadata'. Sample data: 12002, 12003.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, dialogErrorCode: number, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.oAuthDialogError.code, $.extend(true, { TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ DialogErrorCode: dialogErrorCode }) }, options));
        }
    }

    /** Contains functionality for the "OAuthSignoutDialogError" operation. The OAuth signout dialog encountered an error. */
    public readonly oAuthSignoutDialogError = {

        /** The "OAuthSignoutDialogError" error code. */
        get code() { return "OAuthSignoutDialogError"; },

        /**
         * Adds the "OAuthSignoutDialogError" error.
         * @param message The error message to add.
         * @param dialogErrorCode The error code encountered Telemetry classification of 'System Metadata'. Sample data: 12002, 12003.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, dialogErrorCode: number, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.oAuthSignoutDialogError.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ DialogErrorCode: dialogErrorCode }) }, options));
        }
    }

    /** Contains functionality for the "OAuthDialogOpenError" operation. The OAuth dialog encountered an error while trying to open. */
    public readonly oAuthDialogOpenError = {

        /** The "OAuthDialogOpenError" error code. */
        get code() { return "OAuthDialogOpenError"; },

        /**
         * Adds the "OAuthDialogOpenError" error.
         * @param message The error message to add.
         * @param dialogError The JSON serialized error encountered Telemetry classification of 'System Metadata'. Sample data: {"name":"Display Dialog Error","message":"The operation failed because this add-in already has an active dialog.","code":12007}, {"name":"Index out of range.","message":"Index out of range.","code":12001}.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, dialogError: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.oAuthDialogOpenError.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ DialogError: dialogError }) }, options));
        }
    }

    /** Contains functionality for the "OAuthSignoutPopupError" operation. OAuth signout via MSAL popup API encountered an error. */
    public readonly oAuthSignoutPopupError = {

        /** The "OAuthSignoutPopupError" error code. */
        get code() { return "OAuthSignoutPopupError"; },

        /**
         * Adds the "OAuthSignoutPopupError" error.
         * @param message The error message to add.
         * @param errorCode The error code encountered Telemetry classification of 'System Metadata'. Sample data: user_cancelled, interaction_required.
         * @param error The JSON serialized exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: BrowserAuthError: user_cancelled: User cancelled the flow.    at BrowserAuthError.AuthError [as constructor] (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:10535:24)    at new BrowserAuthError (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:3956:28)    at Function.push.../../node_modules/@azure/msal-browser/dist/error/BrowserAuthError.js.BrowserAuthError.createUserCancelledError (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:4045:16)    at https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:6207:101.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, errorCode: string, error: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.oAuthSignoutPopupError.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ ErrorCode: errorCode, Error: error }) }, options));
        }
    }

    /** Contains functionality for the "OAuthSignoutInlineError" operation. Inline OAuth signout encountered an error. */
    public readonly oAuthSignoutInlineError = {

        /** The "OAuthSignoutInlineError" error code. */
        get code() { return "OAuthSignoutInlineError"; },

        /**
         * Adds the "OAuthSignoutInlineError" error.
         * @param message The error message to add.
         * @param errorCode The error code encountered Telemetry classification of 'System Metadata'. Sample data: user_cancelled, interaction_required.
         * @param error The JSON serialized exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: BrowserAuthError: user_cancelled: User cancelled the flow.    at BrowserAuthError.AuthError [as constructor] (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:10535:24)    at new BrowserAuthError (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:3956:28)    at Function.push.../../node_modules/@azure/msal-browser/dist/error/BrowserAuthError.js.BrowserAuthError.createUserCancelledError (https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:4045:16)    at https://usnconeboxax1aos.cloud.onebox.dynamics.com/SharedOfficeApp/vendors-node_modules_azure_msal-browser_dist_index_js-node_modules_jquery_dist_jquery_js.5fa455841f790a9a1b65.bundle.js:6207:101.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, errorCode: string, error: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.oAuthSignoutInlineError.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ ErrorCode: errorCode, Error: error }) }, options));
        }
    }

    /** Contains functionality for the "CookieAuthenticationFailed" operation. Cookie authentication failed. */
    public readonly cookieAuthenticationFailed = {

        /** The "CookieAuthenticationFailed" error code. */
        get code() { return "CookieAuthenticationFailed"; },

        /**
         * Shows the "CookieAuthenticationFailed" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.cookieAuthenticationFailed.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "NoAuthenticationRealm" operation. Server config indicated OAuth usage, but no authentication realm was provided. */
    public readonly noAuthenticationRealm = {

        /** The "NoAuthenticationRealm" error code. */
        get code() { return "NoAuthenticationRealm"; },

        /**
         * Shows the "NoAuthenticationRealm" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.noAuthenticationRealm.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "NoClientID" operation. Server config indicated OAuth usage, but no client ID was provided. */
    public readonly noClientID = {

        /** The "NoClientID" error code. */
        get code() { return "NoClientID"; },

        /**
         * Shows the "NoClientID" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.noClientID.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "NoAuthenticationResourceID" operation. Server config indicated OAuth usage, but no authentication resource ID was provided. */
    public readonly noAuthenticationResourceID = {

        /** The "NoAuthenticationResourceID" error code. */
        get code() { return "NoAuthenticationResourceID"; },

        /**
         * Shows the "NoAuthenticationResourceID" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.noAuthenticationResourceID.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "NoAuthenticationContext" operation. The MSAL JS library failed to load and AuthenticationContext is now undefined, preventing authentication from proceeding. */
    public readonly noAuthenticationContext = {

        /** The "NoAuthenticationContext" error code. */
        get code() { return "NoAuthenticationContext"; },

        /**
         * Shows the "NoAuthenticationContext" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.noAuthenticationContext.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "AuthDialogLoginError" operation. Authentication failed on the auth dialog. */
    public readonly authDialogLoginError = {

        /** The "AuthDialogLoginError" error code. */
        get code() { return "AuthDialogLoginError"; },

        /**
         * Shows the "AuthDialogLoginError" error.
         * @param message The error message to show.
         * @param authLoginError The authentication error code. Telemetry classification of 'System Metadata'. Sample data: AADSTS50058, Authentication error, AADSTS70002.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, authLoginError: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.authDialogLoginError.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ AuthLoginError: authLoginError }) }, options));
        }
    }

    /** Contains functionality for the "LoginError" operation. Authentication failed during the main auth flow. */
    public readonly loginError = {

        /** The "LoginError" error code. */
        get code() { return "LoginError"; },

        /**
         * Shows the "LoginError" error.
         * @param message The error message to show.
         * @param authLoginError The authentication error code. Telemetry classification of 'System Metadata'. Sample data: Authentication error, AADSTS70002.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, authLoginError: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.loginError.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ AuthLoginError: authLoginError }) }, options));
        }
    }

    /** Contains functionality for the "OAuthAuthenticationFailed" operation. Authentication failed during the main auth flow while trying to retrieve the token. */
    public readonly oAuthAuthenticationFailed = {

        /** The "OAuthAuthenticationFailed" error code. */
        get code() { return "OAuthAuthenticationFailed"; },

        /**
         * Shows the "OAuthAuthenticationFailed" error.
         * @param message The error message to show.
         * @param authError The authentication error code. Telemetry classification of 'System Metadata'. Sample data: AADSTS50058, Authentication error, AADSTS70002.
         * @param msalErrorCode The MSAL error code. Telemetry classification of 'System Metadata'. Sample data: interaction_required, Token renewal failed.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, authError: string, msalErrorCode: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.oAuthAuthenticationFailed.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ AuthError: authError, MsalErrorCode: msalErrorCode }) }, options));
        }
    }

    /** Contains functionality for the "NotAuthorized" operation. The user has been determined to not have authorization to use the system. */
    public readonly notAuthorized = {

        /** The "NotAuthorized" error code. */
        get code() { return "NotAuthorized"; },

        /**
         * Shows the "NotAuthorized" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.notAuthorized.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "UnhandledAppError" operation. An unhandled exception was encountered. */
    public readonly unhandledAppError = {

        /** The "UnhandledAppError" error code. */
        get code() { return "UnhandledAppError"; },

        /**
         * Shows the "UnhandledAppError" error.
         * @param message The error message to show.
         * @param msg The exception's message. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: Unable to get property 'isValid' of undefined or null reference, Unable to get property 'closed' of undefined or null reference.
         * @param urlLocation The location of the file from which the exception originated. Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js, https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js.
         * @param line The line number within the file from which the exception originated. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         * @param column The column number within the file from which the exception originated. Telemetry classification of 'System Metadata'. Sample data: 5, 125, 8150.
         * @param error The JSON serialized exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: {"description":"Unable to get property 'isValid' of undefined or null reference","number":-2146823281,"stack":"TypeError: Unable to get property 'isValid' of undefined or null reference\n   at ui (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:15438)\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:1944)\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:19712)\n   at OSF.DDA.issueAsyncResult (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:58174)\n   at onComplete (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69650)\n   at Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:95016)\n   at e[] (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69387)\n   at Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/exce, {"description":"Unable to get property 'closed' of undefined or null reference","number":-2146823281,"stack":"TypeError: Unable to get property 'closed' of undefined or null reference\n   at Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:37:5273)"}.
         * @param stack The stack trace for the encountered exception. Telemetry classification of 'End User Pseudonymized Information (EUPI)'. Sample data: TypeError: Unable to get property 'isValid' of undefined or null reference\nat ui (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:15438)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:1944)\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:43:19712)\nat OSF.DDA.issueAsyncResult (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:58174)\nat onComplete (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69650)\nat Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:95016)\nat e[] (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:69387)\nat Anonymous function (https://appsforoffice.microsoft.com/lib/1.1/hosted/excel-win32-16.01.js:21:77100)\nat ci (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:4, TypeError: Unable to get property 'closed' of undefined or null reference\nat Anonymous function (https://officeapps.dynamics.com/dynamicsofficeapp/v1.3.0.0/App/DynamicsApp.min.js:37:5273).
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, msg: string, urlLocation: string, line: number, column: number, error: string, stack: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.unhandledAppError.code, $.extend(true, { TelemetryProperties: util.telemetry.createErrorTelemetryOptions({ Message: msg, URLLocation: urlLocation, Line: line, Column: column, Error: error, Stack: stack }) }, options));
        }
    }

    /** Contains functionality for the "OfficeApplicationNotRecognized" operation. The Office application couldn't be recognized (e.g. Excel, Word, etc...). */
    public readonly officeApplicationNotRecognized = {

        /** The "OfficeApplicationNotRecognized" error code. */
        get code() { return "OfficeApplicationNotRecognized"; },

        /**
         * Shows the "OfficeApplicationNotRecognized" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.officeApplicationNotRecognized.code, $.extend(true, {}, options));
        }
    }

    /** Contains functionality for the "ConfigurationRetrievalFailed" operation. Failed to retrieve server configuration. */
    public readonly configurationRetrievalFailed = {

        /** The "ConfigurationRetrievalFailed" error code. */
        get code() { return "ConfigurationRetrievalFailed"; },

        /**
         * Shows the "ConfigurationRetrievalFailed" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.configurationRetrievalFailed.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "ConfigurationServiceFailed" operation. The direct call to the server configuration service failed. */
    public readonly configurationServiceFailed = {

        /** The "ConfigurationServiceFailed" error code. */
        get code() { return "ConfigurationServiceFailed"; },

        /**
         * Adds the "ConfigurationServiceFailed" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.configurationServiceFailed.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "ConfigurationResourceFailed" operation. The call to the server configuration resource failed. */
    public readonly configurationResourceFailed = {

        /** The "ConfigurationResourceFailed" error code. */
        get code() { return "ConfigurationResourceFailed"; },

        /**
         * Adds the "ConfigurationResourceFailed" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.configurationResourceFailed.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "RegistrationRetrievalFailed" operation. Failed to retrieve applet and resource registrations. */
    public readonly registrationRetrievalFailed = {

        /** The "RegistrationRetrievalFailed" error code. */
        get code() { return "RegistrationRetrievalFailed"; },

        /**
         * Shows the "RegistrationRetrievalFailed" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.registrationRetrievalFailed.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "InvalidHostUrlEntered" operation. An invalid host URL was entered in the options dialog. */
    public readonly invalidHostUrlEntered = {

        /** The "InvalidHostUrlEntered" error code. */
        get code() { return "InvalidHostUrlEntered"; },

        /**
         * Shows the "InvalidHostUrlEntered" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.invalidHostUrlEntered.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "UnableToSaveSettings" operation. Saving settings failed. */
    public readonly unableToSaveSettings = {

        /** The "UnableToSaveSettings" error code. */
        get code() { return "UnableToSaveSettings"; },

        /**
         * Shows the "UnableToSaveSettings" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.unableToSaveSettings.code, $.extend(true, { TelemetryProperties: util.telemetry.createOfficeErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "UnknownAppMessageTypeEncountered" operation. An unknown message type was encountered */
    public readonly unknownAppMessageTypeEncountered = {

        /** The "UnknownAppMessageTypeEncountered" error code. */
        get code() { return "UnknownAppMessageTypeEncountered"; },

        /**
         * Shows the "UnknownAppMessageTypeEncountered" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.unknownAppMessageTypeEncountered.code, $.extend(true, {}, options));
        }
    }

    /** Contains functionality for the "OfficeVersionRequirementsNotMet" operation. The Office version requirements weren't met for the app (e.g. Office 2016). */
    public readonly officeVersionRequirementsNotMet = {

        /** The "OfficeVersionRequirementsNotMet" error code. */
        get code() { return "OfficeVersionRequirementsNotMet"; },

        /**
         * Shows the "OfficeVersionRequirementsNotMet" error.
         * @param message The error message to show.
         * @param required The version of Office that is required for the app to operation properly. Telemetry classification of 'System Metadata'. Sample data: 2016.
         * @param actual The version of Office that the app is currently running within. Telemetry classification of 'System Metadata'. Sample data: 2013.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, required: number, actual: number, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.officeVersionRequirementsNotMet.code, $.extend(true, { TelemetryProperties: util.telemetry.createUserErrorTelemetryOptions({ Required: required, Actual: actual }) }, options));
        }
    }

    /** Contains functionality for the "NoAppletRegistrationsRetrieved" operation. Registration retrieval was successful, but no applet registrations were retrieved. */
    public readonly noAppletRegistrationsRetrieved = {

        /** The "NoAppletRegistrationsRetrieved" error code. */
        get code() { return "NoAppletRegistrationsRetrieved"; },

        /**
         * Adds the "NoAppletRegistrationsRetrieved" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.noAppletRegistrationsRetrieved.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "AppletRegistrationHadInvalidUrl" operation. An applet registration contained an invalid URL (e.g. not relative). */
    public readonly appletRegistrationHadInvalidUrl = {

        /** The "AppletRegistrationHadInvalidUrl" error code. */
        get code() { return "AppletRegistrationHadInvalidUrl"; },

        /**
         * Adds the "AppletRegistrationHadInvalidUrl" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.appletRegistrationHadInvalidUrl.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "ResourceRegistrationHadInvalidUrl" operation. A resource registration contained an invalid URL (e.g. not relative). */
    public readonly resourceRegistrationHadInvalidUrl = {

        /** The "ResourceRegistrationHadInvalidUrl" error code. */
        get code() { return "ResourceRegistrationHadInvalidUrl"; },

        /**
         * Adds the "ResourceRegistrationHadInvalidUrl" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.resourceRegistrationHadInvalidUrl.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "DuplicateResourceRegistrationEncountered" operation. Two or more resources were registered with the same ID. */
    public readonly duplicateResourceRegistrationEncountered = {

        /** The "DuplicateResourceRegistrationEncountered" error code. */
        get code() { return "DuplicateResourceRegistrationEncountered"; },

        /**
         * Adds the "DuplicateResourceRegistrationEncountered" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.duplicateResourceRegistrationEncountered.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "AppletToLoadNotRegistered" operation. An attempt was made to load an applet that wasn't registered. */
    public readonly appletToLoadNotRegistered = {

        /** The "AppletToLoadNotRegistered" error code. */
        get code() { return "AppletToLoadNotRegistered"; },

        /**
         * Adds the "AppletToLoadNotRegistered" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.appletToLoadNotRegistered.code, $.extend(true, { LogTelemetry: true}, options));
        }
    }

    /** Contains functionality for the "UnableToUpdateAppletOptions" operation. An applet's options were attempted to be updated, but the request timed out. */
    public readonly unableToUpdateAppletOptions = {

        /** The "UnableToUpdateAppletOptions" error code. */
        get code() { return "UnableToUpdateAppletOptions"; },

        /**
         * Adds the "UnableToUpdateAppletOptions" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.unableToUpdateAppletOptions.code, $.extend(true, { LogTelemetry: true}, options));
        }
    }

    /** Contains functionality for the "AppletLabelLocalizationFailed" operation. An applet's label failed to localize. */
    public readonly appletLabelLocalizationFailed = {

        /** The "AppletLabelLocalizationFailed" error code. */
        get code() { return "AppletLabelLocalizationFailed"; },

        /**
         * Adds the "AppletLabelLocalizationFailed" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.appletLabelLocalizationFailed.code, $.extend(true, { LogTelemetry: true, TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "CommonAppletConfigRetrievalFailed" operation. Retrieving common applet configuration failed. */
    public readonly commonAppletConfigRetrievalFailed = {

        /** The "CommonAppletConfigRetrievalFailed" error code. */
        get code() { return "CommonAppletConfigRetrievalFailed"; },

        /**
         * Adds the "CommonAppletConfigRetrievalFailed" error.
         * @param message The error message to add.
         * @param options The error options to control the adding of the message.
         */
        add: (message: string, options?: ErrorOptions): void => {
            this.errorManager.addErrorNotification(message, this.commonAppletConfigRetrievalFailed.code, $.extend(true, { LogTelemetry: true}, options));
        }
    }

    /** Contains functionality for the "UnableToDiscoverEnvironment" operation. Discovering an environment via discovery service call failed. */
    public readonly unableToDiscoverEnvironment = {

        /** The "UnableToDiscoverEnvironment" error code. */
        get code() { return "UnableToDiscoverEnvironment"; },

        /**
         * Shows the "UnableToDiscoverEnvironment" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.unableToDiscoverEnvironment.code, $.extend(true, { TelemetryProperties: util.telemetry.createSystemErrorTelemetryOptions({  }) }, options));
        }
    }

    /** Contains functionality for the "DiscoveryServiceCallFailed" operation. Calling the discovery service failed. */
    public readonly discoveryServiceCallFailed = {

        /** The "DiscoveryServiceCallFailed" error code. */
        get code() { return "DiscoveryServiceCallFailed"; },

        /**
         * Shows the "DiscoveryServiceCallFailed" error.
         * @param message The error message to show.
         * @param options The error options to control the showing of the message.
         */
        show: (message: string, options?: ShowErrorOptions): void => {
            this.errorManager.showError(message, this.discoveryServiceCallFailed.code, $.extend(true, {}, options));
        }
    }

    /**
     * @param errorManager The object used to manage errors.
     */
    constructor(private errorManager: ErrorManager) { }
}

export interface TelemetryProperties {

    /** The name of the applicationTelemetry classification of 'System Metadata'. Sample data: Microsoft Dynamics Office Web Addin. */
    Application?: string;

    /** The app session's unique ID used to correlate all telemetry for that session.Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF. */
    CorrelationID?: string;

    /** The app session's unique ID used to correlate all telemetry for that session.Telemetry classification of 'System Metadata'. Sample data: 11111111-2222-3333-4444-DEFABCDEFABC, 55555555-6677-8888-9999-ABCDEFABCDEF. */
    RunCorrelationID?: string;

    /** The language the app is running with.Telemetry classification of 'System Metadata'. Sample data: en-US, ES, NL. */
    Language?: string;

    /** The version of Office that is hosting the app (e.g. 2013, 2016, etc...).Telemetry classification of 'System Metadata'. Enumeration type of 'util.OfficeVersion'. Sample data: 2013, 2016. */
    OfficeVersion?: string | number;

    /** The type of Office application that is hosting the app (e.g. Word, Excel, etc...).Telemetry classification of 'System Metadata'. Sample data: Excel, Word. */
    CurrentApplication?: string;

    /** The type of Office application client that is hosting the app (e.g. Word, Excel, etc...).Telemetry classification of 'System Metadata'. Sample data: Desktop, Web. */
    ClientType?: string;

    /** The host with which the app is interacting.Telemetry classification of 'Organizational Identifiable Information (OII)'. Sample data: https://usnconeboxax1aos.cloud.onebox.dynamics.com. */
    Host?: string;

    /** The source from which the telemetry originated (e.g. blank for app or the applet's ID).Telemetry classification of 'System Metadata'. Sample data: Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet, Microsoft.Dynamics.Platform.Integration.Office.WordDesignerApplet. */
    SourceID?: string;

    /** The applet's build version; only applied on telemetry events originating from applets.Telemetry classification of 'System Metadata'. Sample data: 7.0.17193.02, dev-20170824. */
    AppletVersion?: string;

    /** The applet's build type (e.g. debug or release); only applied on telemetry events originating from applets.Telemetry classification of 'System Metadata'. Sample data: debug, release. */
    AppletBuildType?: string;
}

/** Provides mechanisms for performing telemetry related functions. */
export class TelemetryService {

    /** The available operations and mechanisms to begin, update, and end them. */
    public operations: OperationHandler;

    /** The available events and mechanisms to log them. */
    public events: EventHandler;

    /** The available errors and mechanisms to show or add them. */
    public errors: ErrorHandler;

    /** Initializes the telemetry operations, events, and errors. Prior to initialization, members are not defined. */
    public initialize(
        operationManager: OperationManager,
        eventLogger: EventLogger,
        errorManager: ErrorManager): void {
        this.operations = new OperationHandler(operationManager);
        this.events = new EventHandler(eventLogger);
        this.errors = new ErrorHandler(errorManager);
    }
}

/** The available operation, event, and error telemetry. */
export const telemetry = new TelemetryService();
